import React, { useRef, useState } from 'react';
import ContactNameFields from '../shared/ContactNameFields';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import ContactCommunications from '../clients/ContactCommunications';
import { formatPhone } from '../../lib/phone';
import LabeledMultiField from '../shared/LabeledMultiField';
import { accountNameLabel } from '../../lib/companyContactAdd';

function ContactDetailsForm({
    dialogLabel,
    wrapperClassName,
    intro = '',
    idPrefix = 'contact-details',
    name,
    accountName,
    accountKind = 'business',
    phones = [],
    emails = [],
    readOnlyNotice = '',
    nameOnlyNotice = '',
    primaryMethod = null,
    emailError = '',
    phoneError = '',
    contact = null,          // BL-177 Step 2m: the existing CRM contact, whose communication choices show READ-ONLY (they belong to the client)
    consentReadOnly = false,
    canSubmit,
    submitting = false,
    submitLabel,
    submitTooltip,
    destructiveLabel = '',
    destructiveTooltip = '',
    destructiveDisabled = false,
    onDestructive,
    cancelLabel = 'Cancel',
    onNameChange,
    onAccountNameChange,
    onAccountKindChange,
    onEmailsChange,
    onPhonesChange,
    onCancel,
    onSubmit,
    theme = 'light',
}) {
    const [saveError, setSaveError] = useState('');
    const [accountError, setAccountError] = useState('');
    const errorRef = useRef(null);
    const accountInputRef = useRef(null);
    const submit = async () => {
        setSaveError('');
        // Locked hotfix (item 9): Save stays enabled for a Business contact with no company. A click says what is missing, on the
        // field itself, the same way the CRM Add Client form does - it never sits there silently disabled.
        if (accountKind !== 'personal' && !String(accountName || '').trim()) {
            setAccountError('Enter a company name, or choose Personal for an individual contact.');
            requestAnimationFrame(() => accountInputRef.current?.focus?.());
            return;
        }
        setAccountError('');
        try { await onSubmit?.(); } catch (failure) {
            setSaveError(failure?.message || 'Could not save this contact. Please try again.');
            requestAnimationFrame(() => errorRef.current?.scrollIntoView?.({ block: 'nearest' }));
        }
    };
    const dark = theme === 'dark';
    const labelCls = dark ? 'text-gray-400' : 'text-gray-500';
    const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${dark
        ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500'
        : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`;
    const helperCls = `mt-1 text-[11px] leading-snug ${labelCls}`;
    const alertMessageCls = 'mt-1 text-xs text-[var(--ui-alert)]';
    const actionCls = 'px-2 py-1 rounded-md text-xs font-medium text-white bg-[var(--accent-ink)] disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryCls = `rounded-lg border px-3 py-1.5 text-xs ${dark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`;
    const primaryKind = primaryMethod?.kind;
    const personal = accountKind === 'personal';
    const locked = Boolean(readOnlyNotice);
    // name-only: the account type/name and the person's name stay editable; phones and emails are an admin's
    const detailsLocked = locked || Boolean(nameOnlyNotice);

    return (
        <div role="dialog" aria-label={dialogLabel} className={wrapperClassName}>
            {intro && <p className="mb-3 text-xs font-medium text-[var(--ink)]">{intro}</p>}
            <div className="space-y-3">
                <div>
                    <ContactNameFields firstName={contact?.first_name} lastName={contact?.last_name} value={name} onChange={onNameChange} className={inputCls} labelClassName={`block text-xs font-semibold ${labelCls}`} idPrefix={idPrefix} />
                    <p className={helperCls}>Pre-filled — click in and type over it to identify who this is.</p>
                </div>
                <div>
                    <label htmlFor={`${idPrefix}-kind`} className={`block text-xs font-semibold ${labelCls}`}>Account type</label>
                    <select
                        id={`${idPrefix}-kind`}
                        value={accountKind}
                        onChange={(event) => { setAccountError(''); onAccountKindChange?.(event.target.value); }}
                        disabled={locked}
                        className={inputCls}
                    >
                        <option value="business">Business — named after the company</option>
                        <option value="personal">Personal — named after the person</option>
                    </select>
                    {personal && <p className={helperCls}>A Personal account is a company client who is an individual. It is named after the person. It is not “My Contacts”.</p>}
                </div>
                {/* Locked hotfix: a Personal account is named after the person (first + last name), so it has no second "Last name" box. */}
                {!personal && <div>
                    <label htmlFor={`${idPrefix}-account`} className={`block text-xs font-semibold ${labelCls}`}>{accountNameLabel(accountKind)} *</label>
                    <input
                        ref={accountInputRef}
                        id={`${idPrefix}-account`}
                        type="text"
                        value={accountName}
                        onChange={(event) => { setAccountError(''); onAccountNameChange?.(event.target.value); }}
                        disabled={locked}
                        aria-invalid={Boolean(accountError)}
                        aria-describedby={accountError ? `${idPrefix}-account-error` : undefined}
                        className={`${inputCls} disabled:opacity-70`}
                    />
                    {accountError && <p id={`${idPrefix}-account-error`} role="alert" className={alertMessageCls}>{accountError}</p>}
                </div>}
                {primaryMethod && (
                    <div className={`rounded-lg border px-3 py-2 text-xs ${dark ? 'border-gray-600 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
                        <div className={`font-semibold ${labelCls}`}>{primaryMethod.kind === 'phone' ? 'Phone' : 'Email'}</div>
                        <div className="mt-0.5 break-all text-[var(--ink)]">{primaryMethod.kind === 'phone' ? formatPhone(primaryMethod.address) : primaryMethod.address}</div>
                    </div>
                )}
                <div>
                    <div className={`block text-xs font-semibold ${labelCls}`}>Emails{primaryMethod ? ' (optional)' : ''}</div>
                    <LabeledMultiField kind="email" ariaLabel="Email" idPrefix={`${idPrefix}-email`} entries={emails} disabled={detailsLocked}
                        onChange={(rows) => onEmailsChange?.(rows)} />
                    {emailError && <p className={alertMessageCls} role="alert">⚠ {emailError}</p>}
                </div>
                <div>
                    <div className={`block text-xs font-semibold ${labelCls}`}>Phones{primaryMethod ? ' (optional)' : ''}</div>
                    <LabeledMultiField kind="phone" ariaLabel="Phone" idPrefix={`${idPrefix}-phone`} entries={phones} disabled={detailsLocked}
                        onChange={(rows) => onPhonesChange?.(rows)} />
                    {phoneError && <p className={alertMessageCls} role="alert">⚠ {phoneError}</p>}
                </div>
                {(readOnlyNotice || nameOnlyNotice) && <p className={helperCls} role="note">{readOnlyNotice || nameOnlyNotice}</p>}
                {consentReadOnly && <p className={helperCls}>Text preferences are read-only for this contact.</p>}
                {contact?.id
                    ? <ContactCommunications contact={contact} theme={dark ? 'dark' : 'light'} idPrefix={`${idPrefix}-comms`} />
                    : <p className={helperCls}>Texts and email choices belong to the client. They set their own in the client app.</p>}
                {saveError && <p ref={errorRef} role="alert" className={alertMessageCls}>{saveError}</p>}
                <div className="flex flex-wrap justify-end gap-2 pt-1">
                    <button type="button" onClick={onCancel} className={secondaryCls}>
                        {cancelLabel}
                    </button>
                    {onDestructive && destructiveLabel && (
                        <SupportWriteGuard mode="disable" tooltip={destructiveTooltip}>
                            <button
                                type="button"
                                onClick={onDestructive}
                                disabled={destructiveDisabled}
                                className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${dark ? 'border-red-500/60 text-red-300 hover:bg-red-950/30' : 'border-red-300 text-red-700 hover:bg-red-50'} disabled:cursor-not-allowed disabled:opacity-50`}
                            >
                                {destructiveLabel}
                            </button>
                        </SupportWriteGuard>
                    )}
                    <SupportWriteGuard mode="disable" tooltip={submitTooltip}>
                        <button type="button" disabled={submitting || !canSubmit} onClick={submit} className={actionCls}>
                            {submitLabel}
                        </button>
                    </SupportWriteGuard>
                </div>
            </div>
        </div>
    );
}

export default ContactDetailsForm;
